import moment from 'moment'
import { notify } from 'reapop'
import { useMemo, useState, useCallback } from 'react'
import { DatePicker, Input, Select, Tag, Card, Row, Col, Statistic, message, Popconfirm } from 'antd'
import { default as CustomButton } from 'components/Button'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import useClient from 'hooks/useClient'
import { DATE_TIME_FORMAT, ICONS, PERMISSIONS } from 'utils/constants'
import SortableColumnTitle from 'components/SortableColumnTitle'
import PageTable from 'components/PageTable'
import Icon from 'components/Icon'
import {
  getReferralTransactionsList,
  getReferralStats,
  cancelReferralTransaction,
  exportReferralTransactions,
} from 'api/loyalty'

const { RangePicker } = DatePicker
const { Option } = Select
const { Search } = Input

const REFERRAL_STATUS_COLORS = {
  PENDING: 'orange',
  COMPLETED: 'green',
  CANCELLED: 'red',
}

const Referrals = () => {
  const { t } = useTranslation()
  const { isClientCan } = useClient()
  const dispatch = useDispatch()
  
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [sortType, setSortType] = useState('createdAt')
  const [sortOrder, setSortOrder] = useState('descend')
  const [keySearch, setKeySearch] = useState('')
  const [statusFilter, setStatusFilter] = useState(null)
  const [dateRangeFilter, setDateRangeFilter] = useState(null)

  const isClientCanManageReferrals = isClientCan(PERMISSIONS.UPDATE_CONFIGURATIONS)

  const filters = useMemo(() => {
    const f = {}
    if (statusFilter) f.status = statusFilter
    if (dateRangeFilter) {
      f.startDate = dateRangeFilter[0].format('YYYY-MM-DD')
      f.endDate = dateRangeFilter[1].format('YYYY-MM-DD')
    }
    return f
  }, [statusFilter, dateRangeFilter])

  const {
    data: referralStatsData,
    isFetching: isFetchingStats,
  } = useQuery(
    ['referral-stats'],
    async () => await getReferralStats(),
    {
      retry: 1,
      onError: () => {
        dispatch(
          notify({
            title: t('referrals.messages.statsFailed'),
            status: 'error',
            dismissAfter: 4000,
          }),
        )
      },
    },
  )

  const {
    data: referralTransactionsData,
    isFetching,
    refetch,
  } = useQuery(
    ['referral-transactions', currentPage, pageSize, keySearch, sortType, sortOrder, filters],
    async () =>
      await getReferralTransactionsList({
        page: currentPage,
        pageSize,
        search: keySearch,
        sortBy: sortType,
        sortOrder: sortOrder === 'descend' ? 'DESC' : 'ASC',
        ...filters,
      }),
    {
      retry: 1,
      keepPreviousData: true,
      onError: () => {
        dispatch(
          notify({
            title: t('referrals.messages.loadFailed'),
            status: 'error',
            dismissAfter: 4000,
          }),
        )
      },
    },
  )

  const cancelTransactionMutation = useMutation(cancelReferralTransaction, {
    onSuccess: () => {
      dispatch(
        notify({
          title: t('referrals.messages.cancelled'),
          status: 'success',
          dismissAfter: 2000,
        }),
      )
      refetch()
    },
    onError: () => {
      dispatch(
        notify({
          title: t('referrals.messages.cancelFailed'),
          status: 'error',
          dismissAfter: 4000,
        }),
      )
    },
  })

  const handleCancelTransaction = (transactionId) => {
    cancelTransactionMutation.mutate({ transactionId })
  }

  const handleExport = useCallback(async () => {
    try {
      const blob = await exportReferralTransactions({
        search: keySearch,
        ...filters,
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `referral-transactions-${moment().format('YYYY-MM-DD')}.xlsx`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      message.success(t('referrals.messages.exportSuccess'))
    } catch (error) {
      message.error(t('referrals.messages.exportFailed'))
    }
  }, [keySearch, filters, t])

  const columns = useMemo(() => {
    return [
      {
        title: '№',
        dataIndex: 'id',
        key: 'id',
        align: 'center',
        width: '50px',
        render: (_, record, index) => (currentPage - 1) * pageSize + index + 1,
      },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title={t('referrals.table.referrer')} id="referrerId" />
        ),
        dataIndex: ['referrer', 'fullName'],
        key: 'referrerId',
        sorter: true,
        render: (fullName, record) => (
          <div>
            <div>{fullName || '-'}</div>
            <div style={{ fontSize: '12px', color: '#888' }}>
              {record.referrer?.phoneNumber || '-'}
            </div>
          </div>
        ),
      },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title={t('referrals.table.referred')} id="referredUserId" />
        ),
        dataIndex: ['referredUser', 'fullName'],
        key: 'referredUserId',
        sorter: true,
        render: (fullName, record) => (
          <div>
            <div>{fullName || '-'}</div>
            <div style={{ fontSize: '12px', color: '#888' }}>
              {record.referredUser?.phoneNumber || '-'}
            </div>
          </div>
        ),
      },
      {
        title: t('referrals.table.referralCode'),
        dataIndex: 'referralCode',
        key: 'referralCode',
        width: '150px',
        render: (code) => <code>{code}</code>,
      },
      {
        title: t('referrals.table.referrerPoints'),
        dataIndex: 'referrerPoints',
        key: 'referrerPoints',
        align: 'center',
        width: '120px',
        render: (points) => <strong>{points}</strong>,
      },
      {
        title: t('referrals.table.referredPoints'),
        dataIndex: 'referredPoints',
        key: 'referredPoints',
        align: 'center',
        width: '120px',
        render: (points) => <strong>{points}</strong>,
      },
      {
        title: t('referrals.table.status'),
        dataIndex: 'status',
        key: 'status',
        width: '120px',
        render: (status) => (
          <Tag color={REFERRAL_STATUS_COLORS[status]}>{status}</Tag>
        ),
      },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title={t('referrals.table.createdAt')} id="createdAt" />
        ),
        dataIndex: 'createdAt',
        key: 'createdAt',
        sorter: true,
        width: '180px',
        render: (createdAt) =>
          createdAt ? moment(createdAt).format(DATE_TIME_FORMAT) : '-',
      },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title={t('referrals.table.completedAt')} id="completedAt" />
        ),
        dataIndex: 'completedAt',
        key: 'completedAt',
        sorter: true,
        width: '180px',
        render: (completedAt) =>
          completedAt ? moment(completedAt).format(DATE_TIME_FORMAT) : '-',
      },
      ...(isClientCanManageReferrals
        ? [
            {
              title: t('referrals.table.actions'),
              key: 'actions',
              width: '100px',
              align: 'center',
              render: (_, record) => {
                if (record.status === 'PENDING') {
                  return (
                    <Popconfirm
                      title={t('referrals.confirmCancel')}
                      onConfirm={() => handleCancelTransaction(record.id)}
                      okText={t('app.yes')}
                      cancelText={t('app.no')}
                    >
                      <CustomButton type="link" danger size="small">
                        {t('referrals.actions.cancel')}
                      </CustomButton>
                    </Popconfirm>
                  )
                }
                return '-'
              },
            },
          ]
        : []),
    ]
  }, [t, currentPage, pageSize, isClientCanManageReferrals])

  const tableChangeHandler = (pagination, filter, sorter) => {
    setCurrentPage(pageSize === pagination.pageSize ? pagination.current : 1)
    setPageSize(pagination.pageSize)
    setSortType(sorter.columnKey || null)
    setSortOrder(sorter.order)
  }

  const handleKeySearch = (e) => {
    setKeySearch(e.target.value)
    setCurrentPage(1)
  }

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value)
    setCurrentPage(1)
  }

  const handleDateRangeChange = (dates) => {
    setDateRangeFilter(dates)
    setCurrentPage(1)
  }

  const stats = referralStatsData || {
    totalReferrals: 0,
    completedReferrals: 0,
    pendingReferrals: 0,
    totalPointsAwarded: 0,
  }

  return (
    <div>
      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('referrals.stats.totalReferrals')}
              value={stats.totalReferrals}
              loading={isFetchingStats}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('referrals.stats.completedReferrals')}
              value={stats.completedReferrals}
              valueStyle={{ color: '#3f8600' }}
              loading={isFetchingStats}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('referrals.stats.pendingReferrals')}
              value={stats.pendingReferrals}
              valueStyle={{ color: '#faad14' }}
              loading={isFetchingStats}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('referrals.stats.totalPointsAwarded')}
              value={stats.totalPointsAwarded}
              loading={isFetchingStats}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <Select
          placeholder={t('referrals.filters.status')}
          style={{ width: 150 }}
          value={statusFilter}
          onChange={handleStatusFilterChange}
          allowClear
        >
          <Option value="PENDING">Pending</Option>
          <Option value="COMPLETED">Completed</Option>
          <Option value="CANCELLED">Cancelled</Option>
        </Select>

        <RangePicker
          format={DATE_TIME_FORMAT}
          value={dateRangeFilter}
          onChange={handleDateRangeChange}
        />

        <Search
          placeholder={t('referrals.filters.searchUser')}
          style={{ width: 250 }}
          value={keySearch}
          onChange={handleKeySearch}
          allowClear
        />

        {isClientCanManageReferrals && (
          <CustomButton type="primary" onClick={handleExport}>
            <Icon icon={ICONS.DOWNLOAD} className="btn-icon-suffix" />
            <span className="btn-text">{t('referrals.actions.export')}</span>
          </CustomButton>
        )}
      </div>

      {/* Table */}
      <PageTable
        baseData={referralTransactionsData?.result || []}
        columns={columns}
        loading={isFetching}
        onChange={tableChangeHandler}
        pageTitle={t('referrals.title')}
        pagination={{
          pageSize,
          current: currentPage,
          total: referralTransactionsData?.total || 0,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          showSizeChanger: true,
        }}
      />
    </div>
  )
}

export default Referrals
